import { useTranslation } from "@sagot/i18n";
import { Badge, Checkbox, Input, Switch } from "@sagot/ui";
import { isAlwaysOn, type ActionDraft } from "./setup-config";

const AUDIENCE_TIERS = [1, 2, 3, 4] as const;

export function StarterEditor({ questions, onChange }: { questions: string[]; onChange: (questions: string[]) => void }) {
  const { t } = useTranslation();
  const slots = [0, 1, 2, 3].map((i) => questions[i] ?? "");
  return (
    <fieldset className="space-y-2" data-testid="starter-editor">
      <legend className="text-sm font-bold">{t("onboarding.configure.starters")}</legend>
      {slots.map((question, index) => (
        <Input key={index} aria-label={t("onboarding.configure.starter", { n: index + 1 })} value={question} maxLength={200} onChange={(e) => { onChange(slots.map((q, i) => (i === index ? e.target.value : q))); }} />
      ))}
    </fieldset>
  );
}

interface AudienceEditorProps {
  labels: string[];
  served: number[];
  onLabels: (labels: string[]) => void;
  onServed: (served: number[]) => void;
}

export function AudienceEditor({ labels, served, onLabels, onServed }: AudienceEditorProps) {
  const { t } = useTranslation();
  return (
    <fieldset className="space-y-2" data-testid="audience-editor">
      <legend className="text-sm font-bold">{t("onboarding.configure.audiences")}</legend>
      <p className="text-xs text-fg-muted">{t("onboarding.configure.audiencesHelp")}</p>
      {AUDIENCE_TIERS.map((code, index) => (
        <div key={code} className="flex flex-wrap items-center gap-3">
          <span className="w-5 text-right text-xs tabular-nums text-fg-subtle">{code}</span>
          <Input className="min-w-0 flex-1" aria-label={t("onboarding.configure.audienceLabel", { code })} value={labels[index] ?? ""} maxLength={200} onChange={(e) => { onLabels(labels.map((l, i) => (i === index ? e.target.value : l))); }} />
          <Checkbox
            aria-label={`${t("onboarding.configure.audienceServed")}: ${labels[index] ?? String(code)}`}
            checked={served.includes(code)}
            onCheckedChange={(checked) => { onServed(checked === true ? [...served, code] : served.filter((c) => c !== code)); }}
          />
        </div>
      ))}
    </fieldset>
  );
}

export function ActionToggles({ actions, onChange }: { actions: ActionDraft[]; onChange: (actions: ActionDraft[]) => void }) {
  const { t } = useTranslation();
  return (
    <fieldset className="space-y-2" data-testid="action-toggles">
      <legend className="text-sm font-bold">{t("onboarding.configure.actions")}</legend>
      <p className="text-xs text-fg-muted">{t("onboarding.configure.actionsHelp")}</p>
      {actions.map((action) => {
        const locked = isAlwaysOn(action.key);
        const id = `action-${action.key}`;
        return (
          <div key={action.key} className="flex items-center justify-between gap-3 rounded-lg border border-line p-3">
            <span id={id} className="flex items-center gap-2 text-sm font-semibold">
              {action.name}
              {locked ? <Badge tone="neutral">{t("onboarding.configure.alwaysOn")}</Badge> : null}
            </span>
            <Switch aria-labelledby={id} checked={action.enabled} disabled={locked} onCheckedChange={(on) => { onChange(actions.map((a) => (a.key === action.key ? { ...a, enabled: on } : a))); }} />
          </div>
        );
      })}
    </fieldset>
  );
}
